import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { trackPixel } from "@/lib/pixel";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem("untamed_cart");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem("untamed_cart", JSON.stringify(items));
  }, [items]);

  // Sync cart to backend for abandoned cart tracking (debounced)
  const syncCart = useCallback((cartItems) => {
    const token = localStorage.getItem("untamed_token");
    if (!token || cartItems.length === 0) return;
    api.post("/cart/sync", {
      items: cartItems.map(i => ({
        product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, size: i.size,
      }))
    }).catch(() => {}); // Fire-and-forget
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => syncCart(items), 2000);
    return () => clearTimeout(timer);
  }, [items, syncCart]);

  const addItem = useCallback((product, size, quantity = 1) => {
    const unitPrice = product.is_special && product.special_price ? product.special_price : product.price;
    trackPixel("AddToCart", {
      content_ids: [product.id],
      content_name: product.name,
      content_type: "product",
      value: unitPrice * quantity,
      currency: "ZAR",
    });
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id && i.size === size && !i.bundle_id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, {
        product_id: product.id, name: product.name,
        price: product.is_special && product.special_price ? product.special_price : product.price,
        image_url: product.image_url, size, quantity,
      }];
    });
  }, []);

  // Add a complete bundle: `units` is one entry per physical item the customer will
  // receive — { product_id, name, image_url, size, normal } — so a 2× pack yields two
  // units each with its OWN size. The bundle price is allocated across units proportionally
  // for display; the backend RE-VALIDATES and re-prices server-side on checkout.
  const addBundle = useCallback((bundle, units = []) => {
    if (!units.length) return;
    const totalNormal = units.reduce((s, u) => s + (u.normal || 0), 0) || 1;
    const bundlePrice = bundle.bundle_price || 0;
    let allocated = units.map((u) => Math.round(((u.normal || 0) / totalNormal) * bundlePrice * 100) / 100);
    const diff = Math.round((bundlePrice - allocated.reduce((a, b) => a + b, 0)) * 100) / 100;
    if (allocated.length) allocated[allocated.length - 1] = Math.round((allocated[allocated.length - 1] + diff) * 100) / 100;

    const isKit = bundle.bundle_type === "kit";
    // A kit's exact configuration = its sorted component (product+size) signature, so two
    // kits with DIFFERENT selections stay separate cards while an identical kit stacks qty.
    const kitInstance = isKit
      ? units.map((u) => `${u.product_id}#${u.size}`).sort().join("|")
      : null;

    const newLines = units.map((u, idx) => ({
      product_id: u.product_id,
      name: u.name,
      price: allocated[idx],
      image_url: u.image_url,
      size: u.size,
      quantity: 1,
      bundle_id: bundle.id,
      bundle_name: bundle.name,
      bundle_type: bundle.bundle_type || "fixed",
      component_label: u.role_label || "",
      kit_instance: kitInstance,
      allow_coupon_stacking: !!bundle.allow_coupon_stacking,
    }));

    trackPixel("AddToCart", {
      content_ids: units.map((u) => u.product_id),
      content_name: bundle.name,
      content_type: "product",
      value: bundlePrice,
      currency: "ZAR",
    });

    setItems((prev) => {
      const next = [...prev];
      for (const nl of newLines) {
        const idx = next.findIndex((i) =>
          i.product_id === nl.product_id && i.size === nl.size && i.bundle_id === nl.bundle_id &&
          (nl.kit_instance ? i.kit_instance === nl.kit_instance : !i.kit_instance)
        );
        if (idx >= 0) next[idx] = { ...next[idx], quantity: next[idx].quantity + 1, price: nl.price };
        else next.push(nl);
      }
      return next;
    });
  }, []);

  const removeItem = useCallback((product_id, size, bundleId = null, kitInstance = undefined) => {
    setItems((prev) => prev.filter((i) => !(
      i.product_id === product_id && i.size === size && (i.bundle_id || null) === bundleId &&
      (kitInstance === undefined || (i.kit_instance || null) === kitInstance)
    )));
  }, []);

  const updateQuantity = useCallback((product_id, size, quantity, bundleId = null, kitInstance = undefined) => {
    const match = (i) => i.product_id === product_id && i.size === size && (i.bundle_id || null) === bundleId &&
      (kitInstance === undefined || (i.kit_instance || null) === kitInstance);
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => !match(i)));
      return;
    }
    setItems((prev) => prev.map((i) => (match(i) ? { ...i, quantity } : i)));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    const token = localStorage.getItem("untamed_token");
    if (token) api.post("/cart/sync", { items: [] }).catch(() => {});
  }, []);

  const total = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const itemCount = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, addItem, addBundle, removeItem, updateQuantity, clearCart, total, itemCount }),
    [items, addItem, addBundle, removeItem, updateQuantity, clearCart, total, itemCount]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
